<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { escanearBoleto, escanearGafete } from "@/utils/api_evento";

const router = useRouter();
const resultado = ref(null);
const nombreStaff = localStorage.getItem("staff_nombre") || "Staff";

let scanner = null;
let procesando = false;

function iniciarScanner() {
  scanner = new Html5QrcodeScanner(
    "qr-reader",
    {
      fps: 10,
      qrbox: { width: 260, height: 260 },
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      showTorchButtonIfSupported: true,
      showZoomSliderIfSupported: false,
      rememberLastUsedCamera: true,
    },
    false
  );
  scanner.render(onScanExito, () => {});
}

function destruirScanner() {
  if (scanner) {
    scanner.clear().catch(() => {});
    scanner = null;
  }
}

// Formatea la hora del servidor (UTC ISO) a hora local del navegador
function formatearHoraLocal(horaStr) {
  if (!horaStr) return "—";
  // Si el backend manda HH:MM directamente, lo usamos tal cual
  // Si manda ISO string, convertimos a hora local
  if (horaStr.includes("T") || horaStr.includes("Z")) {
    return new Date(horaStr).toLocaleTimeString("es-MX", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return horaStr;
}

async function onScanExito(token) {
  if (procesando) return;
  procesando = true;

  try {
    const data = await escanearBoleto(token);
    resultado.value = {
      valido: true,
      ...data,
      hora_acceso: formatearHoraLocal(data.hora_acceso),
    };
  } catch (e) {
    const status = e.response?.status;
    const data = e.response?.data || {};

    if (status === 401 || (status === 403 && data.motivo === undefined)) {
      cerrarSesion();
      return;
    }
    if (status === 409) {
      resultado.value = {
        valido: false,
        motivo: "QR ya utilizado",
        nombre: data.nombre,
        ingreso_previo: formatearHoraLocal(data.ingreso_previo),
      };
    } else if (status === 404) {
      // No es un boleto — puede ser un gafete de peleador/asistente
      await intentarEscanearGafete(token);
    } else if (status === 403) {
      resultado.value = { valido: false, motivo: data.motivo || "Boleto no registrado" };
    } else {
      resultado.value = { valido: false, motivo: "Error de conexión" };
    }
  }

  setTimeout(limpiarResultado, 4500);
}

async function intentarEscanearGafete(token) {
  try {
    const data = await escanearGafete(token);
    resultado.value = { valido: true, ...data };
  } catch (e) {
    const status = e.response?.status;
    const data = e.response?.data || {};

    if (status === 401 || (status === 403 && data.motivo === undefined)) {
      cerrarSesion();
      return;
    }
    if (status === 403) {
      resultado.value = { valido: false, motivo: data.motivo || "Gafete no válido" };
    } else {
      resultado.value = { valido: false, motivo: "QR inválido" };
    }
  }
}

function limpiarResultado() {
  resultado.value = null;
  procesando = false;
}

function cerrarSesion() {
  destruirScanner();
  localStorage.removeItem("staff_access");
  localStorage.removeItem("staff_refresh");
  localStorage.removeItem("staff_nombre");
  router.push("/escaner/login");
}

onMounted(iniciarScanner);
onUnmounted(destruirScanner);
</script>

<template>
  <div class="esc-root">
    <!-- Header -->
    <header class="esc-header">
      <div class="esc-brand">
        <img src="@/assets/img/Evolve/EGPro.png" alt="EGPro" class="esc-logo" />
        <div class="esc-brand-text">
          <p class="esc-brand-title">Evolve Grappling Pro</p>
          <p class="esc-brand-sub">Control de acceso</p>
        </div>
      </div>
      <div class="esc-header-right">
        <span class="esc-staff-name">{{ nombreStaff }}</span>
        <button class="esc-btn-salir" @click="cerrarSesion">
          <i class="bi bi-box-arrow-right"></i>
          <span class="esc-btn-text"> Salir</span>
        </button>
      </div>
    </header>

    <!-- Resultado overlay -->
    <transition name="pop">
      <div
        v-if="resultado"
        class="esc-overlay"
        :class="resultado.valido ? 'esc-verde' : 'esc-rojo'"
        @click="limpiarResultado"
      >
        <div class="esc-resultado">
          <!-- Icono grande -->
          <div class="esc-res-icono">
            <i
              :class="
                resultado.valido ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'
              "
            ></i>
          </div>

          <!-- Nombre -->
          <h1 class="esc-res-nombre">
            {{
              resultado.nombre ||
              (resultado.es_fisico && resultado.valido
                ? `Boleto físico #${resultado.numero_asiento}`
                : resultado.valido
                ? "Acceso permitido"
                : "Acceso denegado")
            }}
          </h1>

          <!-- Detalle válido — gafete de peleador / asistente ── -->
          <template
            v-if="
              resultado.valido &&
              (resultado.tipo === 'peleador' || resultado.tipo === 'asistente')
            "
          >
            <div class="esc-res-gafete-badge">
              <i
                :class="
                  resultado.tipo === 'peleador'
                    ? 'bi bi-person-badge-fill'
                    : 'bi bi-people-fill'
                "
              ></i>
              {{ resultado.tipo === "peleador" ? "Peleador" : "Asistente / Esquina" }}
            </div>
            <p v-if="resultado.tipo === 'peleador'" class="esc-res-tipo">
              {{ resultado.rol_display
              }}<span v-if="resultado.equipo"> · {{ resultado.equipo }}</span>
            </p>
            <p v-else class="esc-res-tipo">
              Esquina de {{ resultado.peleador_asociado }}
            </p>
            <img
              v-if="resultado.tipo === 'peleador' && resultado.foto_url"
              :src="resultado.foto_url"
              alt="Foto del peleador"
              class="esc-res-foto"
            />
          </template>

          <!-- Detalle válido — boleto ── -->
          <template v-else-if="resultado.valido">
            <p class="esc-res-tipo">
              {{ resultado.tipo_boleto }}
              <span v-if="resultado.numero_asiento && !resultado.es_fisico">
                · #{{ resultado.numero_asiento }}</span
              >
            </p>

            <!-- Boleto físico (venta en taquilla): anónimo, sin datos que mostrar -->
            <div v-if="resultado.es_fisico" class="esc-res-fisico-badge">
              <i class="bi bi-cash-coin"></i> Venta en taquilla — sin datos registrados
            </div>

            <!-- Boleto digital: nombre, fecha de nacimiento y edad calculada -->
            <template v-else>
              <div v-if="resultado.fecha_nacimiento" class="esc-res-hora-badge">
                <i class="bi bi-calendar-event"></i>
                {{ resultado.fecha_nacimiento }}
                <span v-if="resultado.edad !== null && resultado.edad !== undefined">
                  · {{ resultado.edad }} años</span
                >
              </div>
              <div v-if="resultado.es_menor" class="esc-res-menor-badge">
                <i class="bi bi-person-fill"></i> Menor de edad — verificar identificación
              </div>
              <img
                v-if="resultado.es_menor && resultado.foto_url"
                :src="resultado.foto_url"
                alt="Foto asistente"
                class="esc-res-foto"
              />
            </template>

            <div class="esc-res-hora-badge">
              <i class="bi bi-clock"></i>
              Ingresó a las {{ resultado.hora_acceso }}
            </div>
          </template>

          <!-- Detalle inválido -->
          <template v-else>
            <p class="esc-res-tipo">{{ resultado.motivo }}</p>
            <div v-if="resultado.ingreso_previo" class="esc-res-hora-badge esc-hora-roja">
              <i class="bi bi-clock-history"></i>
              Primer ingreso a las {{ resultado.ingreso_previo }}
            </div>
          </template>

          <p class="esc-res-hint">Toca para continuar</p>
        </div>
      </div>
    </transition>

    <!-- Vista cámara -->
    <div class="esc-camara">
      <div class="esc-qr-wrap">
        <div id="qr-reader"></div>
      </div>
      <p class="esc-hint">
        <i class="bi bi-qr-code-scan"></i>
        Apunta la cámara al código QR del asistente
      </p>
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");
@import url("https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css");

.esc-root {
  min-height: 100vh;
  background: #0c1120;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: "Inter", sans-serif;
}

/* ── Header ─────────────────────────────────────────────────────────── */
/* sticky en vez de fixed: el header reserva su propio espacio en el flujo
   normal del documento, así que su alto puede variar (por ejemplo al
   envolver texto en móvil) sin que se monte sobre el contenido de abajo. */
.esc-header {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  min-height: 60px;
  background: #111827;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 20px;
  z-index: 100;
  box-sizing: border-box;
}

.esc-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0; /* permite que el texto se trunque en vez de desbordar */
  flex: 1 1 auto;
}
.esc-logo {
  height: 34px;
  flex-shrink: 0;
}
.esc-brand-text {
  min-width: 0;
}
.esc-brand-title {
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.esc-brand-sub {
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.esc-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.esc-staff-name {
  color: rgba(255, 255, 255, 0.45);
  font-size: 12px;
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.esc-btn-salir {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  flex-shrink: 0;
}
.esc-btn-salir:hover {
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
}

/* Header muy angosto: quitamos lo secundario para que el logo y el
   título nunca se amontonen entre sí. */
@media (max-width: 480px) {
  .esc-header {
    padding: 8px 14px;
    gap: 8px;
  }
  .esc-logo {
    height: 28px;
  }
  .esc-brand-title {
    font-size: 12px;
  }
  .esc-brand-sub {
    display: none;
  }
  .esc-staff-name {
    display: none;
  }
  .esc-btn-text {
    display: none;
  }
  .esc-btn-salir {
    padding: 6px 8px;
  }
}

/* ── Cámara ──────────────────────────────────────────────────────────── */
.esc-camara {
  width: 100%;
  max-width: 420px;
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.esc-qr-wrap {
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  border: 2px solid rgba(243, 188, 44, 0.35);
  box-shadow: 0 0 40px rgba(243, 188, 44, 0.08);
}

.esc-hint {
  margin-top: 1.25rem;
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ── UI de html5-qrcode (inyectada por JS, fuera del render de Vue) ───
   Se necesita :deep() porque estos nodos no llevan el atributo
   data-v-xxxx que usa el scoping normal de Vue. Los selectores por id
   (#qr-reader__...) son la estructura pública y estable que genera la
   librería; si tu versión difiere, inspecciona el DOM y ajusta el id. */
.esc-qr-wrap :deep(#qr-reader) {
  border: none !important;
  background: #000;
}

.esc-qr-wrap :deep(#qr-reader__scan_region) {
  background: #000;
}

/* Oculta el ícono/logo por defecto que la librería muestra antes de
   iniciar la cámara. */
.esc-qr-wrap :deep(#qr-reader__scan_region > img) {
  display: none;
}

.esc-qr-wrap :deep(#qr-reader__dashboard) {
  background: #111827;
  padding: 14px 16px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.esc-qr-wrap :deep(#qr-reader__dashboard_section) {
  margin-bottom: 8px;
  text-align: center;
}

.esc-qr-wrap :deep(#qr-reader__dashboard_section_csr) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* Oculta el enlace "Scan an Image File" para simplificar la interfaz —
   quítalo si prefieres conservar esa opción. */
.esc-qr-wrap :deep(#qr-reader__dashboard_section_swaplink) {
  display: none;
}

.esc-qr-wrap :deep(select) {
  appearance: none;
  -webkit-appearance: none;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  border-radius: 8px;
  padding: 8px 12px;
  width: 100%;
  max-width: 280px;
}

.esc-qr-wrap :deep(select option) {
  background: #111827;
  color: #fff;
}

.esc-qr-wrap :deep(button) {
  background: rgba(243, 188, 44, 0.12);
  border: 1px solid rgba(243, 188, 44, 0.4);
  color: #f3bc2c;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.esc-qr-wrap :deep(button:hover) {
  background: rgba(243, 188, 44, 0.22);
}

.esc-qr-wrap :deep(span) {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
}

.esc-qr-wrap :deep(a) {
  color: #f3bc2c;
  font-size: 12px;
}

/* ── Overlay resultado ───────────────────────────────────────────────── */
.esc-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  cursor: pointer;
  padding: 2rem;
}

.esc-verde {
  background: #064e2b;
}
.esc-rojo {
  background: #4c0519;
}

.esc-resultado {
  text-align: center;
  color: #fff;
  max-width: 340px;
  width: 100%;
}

.esc-res-icono {
  font-size: 80px;
  line-height: 1;
  margin-bottom: 1.25rem;
}
.esc-verde .esc-res-icono {
  color: #4ade80;
}
.esc-rojo .esc-res-icono {
  color: #f87171;
}

.esc-res-nombre {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: #fff;
}

.esc-res-tipo {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 1rem;
}

.esc-res-hora-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 0.75rem;
}

.esc-hora-roja {
  background: rgba(248, 113, 113, 0.15);
  border-color: rgba(248, 113, 113, 0.3);
  color: #fca5a5;
}

.esc-res-gafete-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(243, 188, 44, 0.15);
  border: 1px solid rgba(243, 188, 44, 0.35);
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #f3bc2c;
  margin-bottom: 0.75rem;
}

.esc-res-fisico-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(148, 163, 184, 0.15);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 0.75rem;
}

.esc-res-menor-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(250, 204, 21, 0.15);
  border: 1px solid rgba(250, 204, 21, 0.3);
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 13px;
  color: #fde68a;
  margin-bottom: 1rem;
}

.esc-res-foto {
  display: block;
  width: 130px;
  height: 150px;
  border-radius: 5%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.3);
  margin: 1rem auto 0.75rem;
}

.esc-res-hint {
  margin-top: 2rem;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
}

/* ── Transición ─────────────────────────────────────────────────────── */
.pop-enter-active {
  animation: popIn 0.18s ease;
}
.pop-leave-active {
  animation: popIn 0.12s ease reverse;
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
